import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Util from 'utility';
import Loading from 'components/Loading/index';
import { Editor, EditorState, ContentState, RichUtils, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import QRCode from 'qrcode.react';
import sha1 from 'js-sha1';
import api from 'api';
import { __ } from 'locale';

import FoodRowSimple from 'components/Recipe/RecipeEditor/FoodRowSimple';
import EvaluationPanelSimple from 'components/EvaluationPanel/EvaluationPanelSimple';
import PanelPurchaseCookbook from 'components/PanelPurchaseCookbook';

import {
    setFoodGroupDescription,
    openModal,
    closeModal,
    showMessage,
} from 'actions/index';

const navBtnCls = 'whitespace-nowrap font-normal text-[1em] bg-[#26AA52] cursor-pointer text-white py-[0.5rem] px-[0.75rem] no-underline rounded text-center ml-[0.5em] hover:bg-[#2bbf5c] hover:text-white [&>i]:mr-[0.5rem]';
const tabLinkCls = (isActive) => `relative top-[1px] z-[2] no-underline cursor-pointer py-[0.75em] px-[1em] border ${isActive ? 'border-[#ccc] border-b-white' : 'border-white border-b-[#ccc]'}`;
const recipeTagsGroupCls = 'flex flex-wrap [&>input[type=checkbox]]:hidden [&>label]:inline-flex [&>label]:items-center [&>label]:cursor-pointer [&>label]:py-[0.35em] [&>label]:px-[0.5em] [&>label]:rounded [&>label]:bg-[#e9f5e5] [&>label]:mr-[4px] [&>label]:mb-[4px] [&>label]:whitespace-nowrap [&>label>span]:inline-flex [&>label>span]:items-center [&>label>span]:justify-center [&>label>span]:w-[1.5em] [&>label>span]:h-[1.5em] [&>label>span]:border [&>label>span]:border-[#26AA52] [&>label>span]:rounded [&>label>span]:mr-[0.5em] [&>label>span]:text-[0.85em] [&>label>span]:bg-white [&>label>span>i]:text-white [&>input:checked+label]:bg-[#26AA52] [&>input:checked+label]:text-white [&>input:checked+label>span>i]:text-[#26AA52]';

function RecipeDetail({ id, beginReload, endReload, showMyRecipes }) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const { globals } = data;

    const getInitialEditorState = () => {
        let shareEditorValue = localStorage.getItem('shareMenuDescription');
        if (!shareEditorValue) shareEditorValue = '<p></p>';
        const blocksFromHTML = convertFromHTML(shareEditorValue);
        const contentBlocks = blocksFromHTML.contentBlocks || [];
        return EditorState.createWithContent(ContentState.createFromBlockArray(contentBlocks, blocksFromHTML.entityMap));
    };

    const [recipe, setRecipe] = useState({});
    const [tab, setTab] = useState('description');
    const [shareDestinationProfile, setShareDestinationProfile] = useState('personal');
    const [activeDescriptionGroup, setActiveDescriptionGroup] = useState('');
    const [newPicture, setNewPicture] = useState(false);
    const [showFullPicture, setShowFullPicture] = useState(false);
    const [loading, setLoading] = useState(true);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [customTags, setCustomTags] = useState({});
    const [newTagTitle, setNewTagTitle] = useState('');
    const [tagRenameId, setTagRenameId] = useState(0);
    const [renameTagTitle, setRenameTagTitle] = useState('');
    const [tagDeleteId, setTagDeleteId] = useState(0);
    const [savingChanges, setSavingChanges] = useState(false);
    const [shareEditorState, setShareEditorState] = useState(getInitialEditorState);
    const [shareRecipeClientId, setShareRecipeClientId] = useState(0);
    const [exportPdfLink, setExportPdfLink] = useState(false);
    const [exportSharingPageLink, setExportSharingPageLink] = useState(false);

    useEffect(() => {
        api('recipes/detail/' + id).then(res => {
            let firstGroupId = 0;
            Object.keys(res.data.groups).map(g => {
                const group = res.data.groups[g];
                if (firstGroupId == 0) firstGroupId = group.id;
            });
            setLoading(false);
            setRecipe(res.data);
            setCustomTags(res.data.customTags);
            setActiveDescriptionGroup(firstGroupId);
        });
    }, []); // eslint-disable-line

    const openBuyCookbook = () => {
        dispatch(closeModal());
        dispatch(openModal({
            'title': 'ZOF kuchařka - koupit plný přístup',
            'class': 'dialog-flexible',
            'content': (
                <PanelPurchaseCookbook onDone={() => dispatch(closeModal())} />
            )
        }));
    };

    const doDelete = () => {
        beginReload();
        api('recipes/delete', { post: true, data: { id: recipe.id } }).then(() => {
            dispatch(closeModal());
            showMyRecipes();
            endReload();
        });
    };

    const addCustomTag = () => {
        const title = newTagTitle;
        if (title.trim() === '') {
            alert("Zadejte prosím název kategorie.");
            return;
        }
        api('recipes/add-custom-tag', { post: true, data: { title } }).then(result => {
            setCustomTags(result.data);
        });
    };

    const renameTag = (e, tagId) => {
        setTagRenameId(tagId);
        setTagDeleteId(0);
        setRenameTagTitle(customTags[tagId]);
    };

    const doRenameTag = () => {
        api('recipes/rename-custom-tag', { post: true, data: { id: tagRenameId, title: renameTagTitle } }).then(result => {
            setCustomTags(result.data);
            setTagRenameId(0);
            setRenameTagTitle('');
        });
    };

    const deleteTag = (e, tagId) => {
        setTagDeleteId(tagId);
        setTagRenameId(0);
    };

    const doDeleteTag = () => {
        api('recipes/delete-custom-tag', { post: true, data: { id: tagDeleteId } }).then(result => {
            setCustomTags(result.data);
            setTagDeleteId(0);
        });
    };

    const saveTags = () => {
        beginReload();
        api('recipes/update-tags', {
            post: true,
            data: {
                id: recipe.id,
                tags: recipe.tags,
                use_in_generator: recipe.profi_use_in_generator
            }
        }).then(() => {
            endReload();
            dispatch(showMessage({ show: true, type: 'success', message: 'Štítky byly uloženy.' }));
        });
    };

    const shareEditorChange = (editorState) => {
        setShareEditorState(editorState);
        localStorage.setItem('shareMenuDescription', stateToHTML(editorState.getCurrentContent()));
    };

    const toggleInlineStyle = (event) => {
        event.preventDefault();
        const style = event.currentTarget.getAttribute('data-style');
        setShareEditorState(prev => RichUtils.toggleInlineStyle(prev, style));
    };

    const onEditChange = (e) => {
        const n = e.target.name;
        const v = e.target.value;
        let tagId;

        switch (n) {
            case 'title':
            case 'gastro_ingredients_origin':
            case 'gastro_grammage':
            case 'gastro_price':
                setRecipe(prev => ({ ...prev, lang: { ...prev.lang, [n]: v } }));
                break;
            case 'description_group':
                setActiveDescriptionGroup(v);
                break;
            case 'description':
                setRecipe(prev => {
                    const newGroups = { ...prev.groups };
                    Object.keys(newGroups).map(g => {
                        if (newGroups[g].id == activeDescriptionGroup) {
                            newGroups[g] = { ...newGroups[g], description: v };
                        }
                    });
                    return { ...prev, groups: newGroups };
                });
                break;
            case 'picture':
                setNewPicture(e.target.files[0]);
                break;
            case 'gastro_is_on_menu':
                setRecipe(prev => ({
                    ...prev,
                    gastro_is_on_menu: 1 - (prev.gastro_is_on_menu === null ? 0 : prev.gastro_is_on_menu)
                }));
                break;
            case 'gastro_dont_show_description':
                setRecipe(prev => ({
                    ...prev,
                    gastro_dont_show_description: 1 - (prev.gastro_dont_show_description === null ? 0 : prev.gastro_dont_show_description)
                }));
                break;
            case 'rename_tag_title':
                setRenameTagTitle(v);
                break;
            case 'new_tag_title':
                setNewTagTitle(v);
                break;
            case 'tag':
                tagId = e.target.getAttribute('data-id') * 1;
                { const checked = e.target.checked;
                setRecipe(prev => ({
                    ...prev,
                    tags: checked ? [tagId] : []
                })); }
                break;
            case 'rtag':
                tagId = e.target.getAttribute('data-id') * 1;
                { const checked = e.target.checked;
                setRecipe(prev => ({
                    ...prev,
                    tags: checked
                        ? [...prev.tags.filter(t => t !== tagId), tagId]
                        : prev.tags.filter(t => t !== tagId)
                })); }
                break;
            case 'share_destination_profile':
                setShareDestinationProfile(v);
                break;
            case 'clientshare_client_id':
                setShareRecipeClientId(v);
                break;
            case 'use_in_generator':
                { const checked = e.target.checked;
                setRecipe(prev => ({ ...prev, profi_use_in_generator: checked ? 1 : 0 })); }
                break;
            default:
                break;
        }
    };

    const saveChanges = () => {
        const recipeJson = JSON.stringify(recipe);
        let fd = new FormData();
        fd.append('picture', newPicture);
        fd.append('recipe', recipeJson);

        setSavingChanges(true);

        api('recipes/update', { post: true, data: fd }).then(res => {
            res = res.data;
            if (typeof res.error !== 'undefined') {
                if (res.error === 'invalid_image_format') {
                    alert(__("Neplatný formát obrázku (nahrávejte pouze JPG nebo PNG soubory)"));
                    setSavingChanges(false);
                    return;
                }
            } else {
                setLoading(true);
                api('recipes/detail/' + id).then(res2 => {
                    let firstGroupId = 0;
                    Object.keys(res2.data.groups).map(g => {
                        const group = res2.data.groups[g];
                        if (firstGroupId == 0) firstGroupId = group.id;
                    });
                    setLoading(false);
                    setSavingChanges(false);
                    setRecipe(res2.data);
                    setActiveDescriptionGroup(firstGroupId);
                });
            }
        }).catch(err => {
            setSavingChanges(false);
            const serverMsg = err?.response?.data?.message || err?.response?.data?.error || JSON.stringify(err?.response?.data);
            alert('Chyba při ukládání (HTTP ' + (err?.response?.status) + '): ' + serverMsg);
        });
    };

    const shareRecipeToClient = () => {
        const clientId = shareRecipeClientId * 1;
        if (clientId == 0) {
            alert("Vyberte uživatele, kterému chcete recept nasdílet.");
            return;
        }
        api('clients/share-recipe-to', { post: true, data: { clientId, recipeId: recipe.id } }).then(() => {
            dispatch(closeModal());
            dispatch(showMessage({ show: true, type: 'success', message: 'Recept byl sdílen uživateli.' }));
        });
    };

    const exportPDF = () => {
        setExportPdfLink(false);
        api('recipes/export-pdf', { post: true, data: { id: recipe.id } }).then(result => {
            setExportPdfLink('https://www.zofapp.cz/data/zofapp/recipe-pdf/' + result.data.filename + '.pdf');
        });
    };

    const exportSharingPage = () => {
        setExportSharingPageLink(false);
        api('recipes/export-sharing-page', { post: true, data: { id: recipe.id } }).then(result => {
            setExportSharingPageLink('https://www.zofapp.cz/r/' + result.data.url);
        });
    };

    if (loading === true) return <Loading />;
    if (!recipe.groups || !recipe.ingredients) return <Loading />;

    const isMyRecipe = recipe.user_id === data.globals.user.id;

    let editButton = false;
    if (isMyRecipe) {
        editButton = <a className={tabLinkCls(tab === 'edit')} style={{marginRight:'.5em'}} onClick={() => setTab('edit')}>{__('Upravit')}</a>;
    }

    let qrButton = false;
    let exportButton = false;
    if (Util.userHasPurchased('ZOF_GASTRO') && recipe.user_id === data.user.id) {
        qrButton = <a className={tabLinkCls(tab === 'qr')} style={{marginRight:'.5em'}} onClick={() => setTab('qr')}>QR kód</a>;
        exportButton = <a className={tabLinkCls(tab === 'export')} style={{marginRight:'.5em'}} onClick={() => setTab('export')}>Data export</a>;
    }

    let shareButton = <a className={tabLinkCls(tab === 'share_export')} style={{marginRight:'.5em'}} onClick={() => setTab('share_export')}>{__('Sdílení/export')}</a>;
    if (Util.isCurrentTaskSeminarWork()) shareButton = false;

    let navbarAside = false;
    if (isMyRecipe) {
        if (!confirmDelete) {
            navbarAside = (
                <aside className="ml-auto">
                    <Link className={navBtnCls} to={`/recipe/open/${recipe.id}`}>{__('Otevřít')}</Link>
                    <a className={navBtnCls} onClick={() => setConfirmDelete(true)}><i className="fa fa-trash"></i> {__('Smazat')}</a>
                </aside>
            );
        } else {
            navbarAside = (
                <aside className="ml-auto">
                    <a className="whitespace-nowrap font-normal text-[1em] bg-[#c00] cursor-pointer text-white py-[0.5rem] px-[0.75rem] no-underline rounded text-center ml-[0.5em] hover:bg-[#e00] [&>i]:mr-[0.5rem]" onClick={doDelete}><i className="fa fa-exclamation-triangle"></i> {__('Potvrdit smazání')}</a>
                    <a className="whitespace-nowrap font-normal text-[1em] bg-[#888] cursor-pointer text-white py-[0.5rem] px-[0.75rem] no-underline rounded text-center ml-[0.5em] hover:bg-[#999] [&>i]:mr-[0.5rem]" onClick={() => setConfirmDelete(false)}><i className="fa fa-undo"></i> {__('Nemazat')}</a>
                </aside>
            );
        }
    } else {
        let navbarAsideLinks = [];
        if (!Util.isCurrentTaskSeminarWork()) {
            navbarAsideLinks.push(<Link key="open" className={navBtnCls} to={`/recipe/open/${recipe.id}`}>{__('Otevřít recept')}</Link>);
            navbarAside = (
                <aside className="ml-auto">
                    {navbarAsideLinks}
                </aside>
            );
        }
    }

    if (Util.isCurrentTaskSeminarWork()) navbarAside = false;

    const picUrl = `https://www.zofapp.cz/data/zof/${recipe.picture}?${recipe.picture_lastmod}`;
    let description = [];
    let numberOfGroups = Object.keys(recipe.groups).length;
    Object.keys(recipe.groups).map(g => {
        const group = recipe.groups[g];
        if (numberOfGroups > 1) {
            description.push('*** ' + group.title + "\n" + group.description + "\n");
        } else {
            description.push(group.description);
        }
    });
    description = description.join("\n");

    let buyCookbookLink = false;
    if (!isMyRecipe && !Util.userHasPurchased('ZOF_COOKBOOK')) {
        description = 'Postup přípravy receptu včetně množství použitých surovin se zobrazí po zakoupení ZOF kuchařky.';
        buyCookbookLink = <p style={{marginTop:'1.5em'}}><a style={{background:'#f90'}} className="whitespace-nowrap font-normal text-base bg-[#26AA52] cursor-pointer text-white px-3 py-2 no-underline rounded text-center hover:bg-[#2bbf5c] hover:text-white" onClick={openBuyCookbook}>Koupit ZOF kuchařku</a></p>;

        var navBar = (
            <div className="flex items-center my-[1.5em] text-[0.9em]">
                <div className="flex items-center gap-[0.5em]">
                </div>
            </div>
        );
    }

    if (!navBar) {
        navBar = (
            <div className="flex items-center my-[1.5em] text-[0.9em]">
                <div className="flex-1 flex items-center border-b border-[#ccc] mr-[1em]">
                    <a className={tabLinkCls(tab === 'description')} style={{marginRight:'.5em'}} onClick={() => setTab('description')}>{__('Detail receptu')}</a>
                    {editButton}
                    {shareButton}
                    {qrButton}
                    {exportButton}
                </div>
                {navbarAside}
            </div>
        );
    }

    let tabContent = [];
    const pictureCls = 'flex-1 relative mb-[1em] cursor-pointer group before:content-[""] before:absolute before:inset-0 before:bg-black/25 before:opacity-0 hover:before:opacity-100 before:pointer-events-none before:rounded';

    if (showFullPicture) {
        tabContent.push(
            <div key="desc" className="flex items-start">
                <div className="flex-1">
                    <div className={pictureCls} onClick={() => setShowFullPicture(false)}>
                        <img className="block w-full h-auto rounded" src={picUrl} alt="" />
                        <span className="hidden group-hover:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[4em] text-white [text-shadow:0_0_24px_rgba(0,0,0,0.7)]"><i className="fa fa-search-minus"></i></span>
                    </div>
                </div>
            </div>
        );
    } else {
        tabContent.push(
            <div key="desc" className="flex items-start">
                <div className="flex-1">
                    <div className={pictureCls} onClick={() => setShowFullPicture(true)}>
                        <img className="block w-full h-auto rounded" src={picUrl} alt="" />
                        <span className="hidden group-hover:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[4em] text-white [text-shadow:0_0_24px_rgba(0,0,0,0.7)]"><i className="fa fa-search-plus"></i></span>
                    </div>
                    <div style={{flexGrow:1}}>
                        <h3 className="font-bold mt-[1.5em] mb-[0.5em] first:mt-0">{__('Ingredience')}:</h3>
                        {Object.keys(recipe.ingredients).map(f => {
                            let food = recipe.ingredients[f];
                            if (typeof food.group_id === 'undefined' || food.group_id == '' || food.group_id == '0') {
                                return <FoodRowSimple key={food.food_id} food={food} />;
                            }
                            return null;
                        })}
                    </div>
                </div>
                <div className="basis-[300px] pl-[2em]">
                    <h3 className="font-bold mt-[1.5em] mb-[0.5em] first:mt-0">{__('Postup přípravy')}:</h3>
                    {Util.parseRecipeDescription(description)}
                    {buyCookbookLink}
                    <h3 className="font-bold mt-[1.5em] mb-[0.5em]">{__('Nutriční hodnoty')}</h3>
                    <EvaluationPanelSimple recipe={recipe} opened={true} style={{marginLeft:'0'}} />
                </div>
            </div>
        );
    }

    tabContent.push(
        <div key="contents" className="flex mt-[1em]">
            <input type="checkbox" id="cb-panel-evaluation" />
        </div>
    );

    if (tab === 'share_export') {

        let shareToClient = false;
        if (recipe.user_has_clients > 0 && isMyRecipe) {
            let clientShareOptions = [];
            clientShareOptions.push(<option value={0}>- Vyberte -</option>);
            data.globals.clients.map(client => {
                clientShareOptions.push(<option value={client.id}>{client.name}</option>);
            });
            shareToClient = (
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
                    <div className="w-full max-w-[680px]">
                        <h2>Sdílet recept uživateli</h2>
                        <table>
                            <tbody>
                                <tr>
                                    <td>Vyberte uživatele:</td>
                                    <td><select name="clientshare_client_id" onChange={onEditChange}>{clientShareOptions}</select></td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td><button onClick={() => shareRecipeToClient()}>Poslat</button></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        let chooseProfileToShareTo = false;
        if (Util.userHasPurchased('ZOF_GASTRO')) {
            chooseProfileToShareTo = (
                <div className="flex items-center gap-[1em]">
                    <span>Sdílet na:</span>
                    <label>
                        <input checked={shareDestinationProfile === 'personal'} onChange={onEditChange} type="radio" name="share_destination_profile" value="personal" /> osobní profil
                    </label>
                    <label>
                        <input checked={shareDestinationProfile === 'gastro'} onChange={onEditChange} type="radio" name="share_destination_profile" value="gastro" /> Gastro profil
                    </label>
                </div>
            );
        }

        let shareToProfile = (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
                <div className="w-full max-w-[680px]">
                    <h2>Sdílet recept na svůj profil</h2>
                    {chooseProfileToShareTo}
                    <p style={{fontSize:'.9em',color:'#444'}}>Napište komentář:</p>
                    <div style={{width:'100%',maxWidth:'640px'}}>
                        <p style={{margin:'0 0 2px 0',fontSize:'.9e'}}>
                            <input type="button" value="B" data-style="BOLD" onMouseDown={toggleInlineStyle} />
                            <input type="button" value="I" data-style="ITALIC" onMouseDown={toggleInlineStyle} style={{fontStyle:'italic'}} />
                        </p>
                        <div style={{border:'1px solid #ccc',padding:'4px'}}>
                            <Editor editorState={shareEditorState} onChange={shareEditorChange} />
                        </div>
                    </div>
                    <p style={{margin:'1.5em 0',textAlign:'center'}}><Link className="whitespace-nowrap font-normal text-base bg-[#26AA52] cursor-pointer text-white px-3 py-2 no-underline rounded text-center hover:bg-[#2bbf5c] hover:text-white" to={`/recipe/share/${recipe.id}/${shareDestinationProfile}`}>Sdílet recept</Link></p>
                </div>
            </div>
        );

        let pdfDownloadLink = false;
        if (exportPdfLink) {
            pdfDownloadLink = <p style={{padding:'0 0 2em 0',margin:'0',fontWeight:'bold'}}><a style={{color:'#4ebe55',textDecoration:'none',padding:'.35em 1em',border:'1px solid #4ebe55',borderRadius:'3px'}} target="_blank" href={exportPdfLink}><i className="fa fa-file-pdf"></i> Stáhnout PDF</a></p>;
        }

        let exportSharingPageLinkEl = false;
        if (exportSharingPageLink) {
            exportSharingPageLinkEl = <p style={{padding:'0 0 2em 0',margin:'0',fontWeight:'bold'}}><a target="_blank" style={{color:'#4ebe55',textDecoration:'none',padding:'.35em 1em',border:'1px solid #4ebe55',borderRadius:'3px'}} href={exportSharingPageLink}><i className="fa fa-file-pdf"></i> Otevřít stránku pro sdílení receptu</a></p>;
        }

        let exportPdfAndSharingPage = false;
        if (Util.userHasPurchased('ZOF_PROFI') || Util.userHasPurchased('ZOF_COOKBOOK') || isMyRecipe) {
            exportPdfAndSharingPage = (
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
                    <div className="w-full max-w-[680px]">
                        <h2>Export PDF</h2>
                        <p>Kliknutím na tlačítko se vytvoří PDF dokument s receptem.</p>
                        {pdfDownloadLink}
                        <p><button className="bg-[#26AA52] text-white py-[0.5em] px-[1em] rounded cursor-pointer border-none hover:bg-[#2dcb62]" onClick={exportPDF}>Vytvořit PDF</button></p>
                        <div style={{margin:'2em 0'}}>
                            <h2>Vytvořit stránku s receptem</h2>
                            <p>Kliknutím na tlačítko se vytvoří veřejná stránka s receptem, kterou můžete sdílet. Stránka bude obsahovat:</p>
                            <ul>
                                <li>jméno autora receptu</li>
                                <li>fotografii receptu</li>
                                <li>ingredience včetně gramáží</li>
                                <li>postup přípravy</li>
                                <li>seznam alergenů</li>
                                <li>nutriční vyhodnocení receptu</li>
                            </ul>
                            {exportSharingPageLinkEl}
                            <p><button className="bg-[#26AA52] text-white py-[0.5em] px-[1em] rounded cursor-pointer border-none hover:bg-[#2dcb62]" onClick={exportSharingPage}>Vytvořit stránku pro sdílení receptu</button></p>
                        </div>
                    </div>
                </div>
            );
        }

        tabContent = (
            <>
            <div style={{margin:'1em 0'}}>{exportPdfAndSharingPage}</div>
            <div style={{margin:'1em 0'}}>{shareToClient}</div>
            <div style={{margin:'1em 0'}}>{shareToProfile}</div>
            </>
        );
    }

    if (tab === 'edit') {

        let currentDescription = '';
        let descriptionGroups = [];
        Object.keys(recipe.groups).map(g => {
            const group = recipe.groups[g];
            descriptionGroups.push(
                <option value={group.id}>{globals.foodGroupTypes[group.type]}: {group.title}</option>
            );
            if (group.id == activeDescriptionGroup) {
                currentDescription = group.description;
            }
        });

        let saveChangesButton = (
            <span><a onClick={saveChanges}>{__('Uložit změny')}</a></span>
        );

        if (savingChanges) {
            saveChangesButton = <span><a className="opacity-50 cursor-not-allowed">{__('Ukládám')}...</a></span>;
        }

        let gastroData = false;
        if (Util.userHasPurchased('ZOF_GASTRO')) {

            let amount = 0;
            let alergens = [];
            Object.keys(recipe.ingredients).map(i => {
                const ingr = recipe.ingredients[i];
                amount += parseFloat(ingr.amount);

                ingr.alergens.map(aid => {
                    if (!alergens.includes(globals.itemAlergens[aid])) alergens.push(globals.itemAlergens[aid]);
                });
            });

            if (alergens.length == 0) alergens = ['žádné'];

            gastroData = (
                <>
                <h3>Gastro údaje</h3>
                <table>
                    <tbody>
                        <tr>
                            <td></td>
                            <td>
                                <label>
                                    <input type="checkbox" onChange={onEditChange} checked={recipe.gastro_is_on_menu === 1} name="gastro_is_on_menu" value={recipe.gastro_is_on_menu} />
                                    {'\u00A0'}zařadit na jídelní lístek
                                </label>
                            </td>
                        </tr>
                        <tr>
                            <td></td>
                            <td>
                                <label>
                                    <input type="checkbox" onChange={onEditChange} checked={recipe.gastro_dont_show_description === 1} name="gastro_dont_show_description" value={recipe.gastro_dont_show_description} />
                                    {'\u00A0'}nezobrazovat popis receptu (postup přípravy)
                                </label>
                            </td>
                        </tr>
                        <tr>
                            <td>Původ surovin</td>
                            <td><textarea style={{height:'10em'}} onChange={onEditChange} name="gastro_ingredients_origin" value={recipe.lang.gastro_ingredients_origin}></textarea></td>
                        </tr>
                        <tr>
                            <td>Množství</td>
                            <td>
                                <input style={{width:'8em',display:'inline'}} type="text" onChange={onEditChange} name="gastro_grammage" value={recipe.lang.gastro_grammage} />
                                {'\u00A0'}(přesné množství: {Math.ceil(amount)} g/ml)
                            </td>
                        </tr>
                        <tr>
                            <td>Cena</td>
                            <td><input style={{width:'8em',display:'inline'}} type="text" onChange={onEditChange} name="gastro_price" value={recipe.lang.gastro_price} /> Kč</td>
                        </tr>
                        <tr>
                            <td>Alergeny</td>
                            <td>{alergens.join(', ')}</td>
                        </tr>
                    </tbody>
                </table>
                </>
            );
        }

        let tags = [];
        Object.values(globals.tagGroupsOrder || {}).map(g => {
            const tg = (globals.tagGroups || {})[g];
            let groupContents = [];
            Object.keys((globals.tagsToGroups || {})[g] || {}).map(io => {
                const tagId = globals.tagsToGroups[g][io];
                groupContents.push(
                    <>
                    <input id={`rtag_${g}_${io}`} type="checkbox" onChange={onEditChange} checked={recipe.tags.includes(tagId)} name="rtag" data-id={tagId} />
                    <label htmlFor={`rtag_${g}_${io}`}>
                        <span><i className="fa fa-check"></i></span>
                        {globals.tags[tagId]}
                    </label>
                    </>
                );
            });
            tags.push(<h4 key={`h_${g}`}>{tg}</h4>);
            tags.push(<div key={`g_${g}`} className={recipeTagsGroupCls}>{groupContents}</div>);
        });

        let ingredients = [];
        Object.keys(recipe.ingredients).map(f => {
            let food = recipe.ingredients[f];
            ingredients.push(<p>{food.title}</p>);
        });

        let useRecipeInGeneratorOption = false;
        if (Util.userHasPurchased('ZOF_PROFI')) {
            useRecipeInGeneratorOption = (
                <div style={{padding:'2em 0 1em 0'}}>
                    <p><b>Použít tento recept v generátoru</b><br />
                    Před zařazením receptu do generátoru je důležité zaškrtnout všechny relevantní a správné štítky.</p>
                    <label><input type="checkbox" name="use_in_generator" onChange={onEditChange} checked={recipe.profi_use_in_generator == 1} /> Použít recept v generátoru jídelníčků</label>
                </div>
            );
        }

        tabContent = (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
                <div className="w-full max-w-[680px]">
                    <h2>{__('Základní údaje')}</h2>
                    <table className="w-full max-w-[680px] [&_td]:py-[4px] [&_td]:pr-[1em] [&_td]:align-top [&_td:first-child]:w-[160px] [&_input[type=text]]:w-full [&_input[type=text]]:py-[4px] [&_input[type=text]]:px-[5px] [&_select]:w-full [&_select]:py-[4px] [&_select]:px-[5px] [&_textarea]:w-full [&_textarea]:h-[10em] [&_textarea]:py-[4px] [&_textarea]:px-[5px]">
                        <tbody>
                            <tr>
                                <td>{__('Název receptu')}</td>
                                <td><input type="text" onChange={onEditChange} name="title" value={recipe.lang.title} /></td>
                            </tr>
                            <tr>
                                <td>{__('Popis receptu')}</td>
                                <td>
                                    <p><select name="description_group" onChange={onEditChange}>{descriptionGroups}</select></p>
                                    <p><textarea name="description" onChange={onEditChange}>{currentDescription}</textarea></p>
                                </td>
                            </tr>
                            <tr>
                                <td>{__('Obrázek receptu')}</td>
                                <td>
                                    <input type="file" name="picture" onChange={onEditChange} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {gastroData}
                    <h2>{__('Štítky')}</h2>
                    <div>
                        <div style={{display:'flex'}}>
                            <div style={{flex:'1'}}>
                                {tags}
                            </div>
                            <div className="text-[0.85em] ml-[1.5em] pl-[1.5em] border-l border-[#ddd] [&>div>p]:mb-[2px]" style={{flexBasis:'200px'}}>
                                <p><b>{__('Ingredience')}:</b></p>
                                <div>{ingredients}</div>
                            </div>
                        </div>
                    </div>
                    {useRecipeInGeneratorOption}
                    <div className="p-[1em] text-center [&>span>a]:inline-block [&>span>a]:py-[0.65em] [&>span>a]:px-[1.25em] [&>span>a]:bg-[#26AA52] [&>span>a]:text-white [&>span>a]:no-underline [&>span>a]:rounded [&>span>a.disabled]:bg-[#aaa]">
                        {saveChangesButton}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
        <div className="px-[0.75em] py-[0.5em] [&>h1]:flex [&>h1]:items-center [&>h1]:justify-between [&>h1]:text-[2em] [&>h1]:font-bold [&>h1]:text-black [&>h1]:mb-[0.5em] [&>h1]:mt-0">
            <h1>{recipe.lang.title}</h1>
            {navBar}
            {tabContent}
        </div>
        </React.Fragment>
    );
}

export default RecipeDetail;
